import { FC, useCallback, useState, MouseEvent, useEffect } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import {
  Button,
  Card,
  Chip,
  Divider,
  Icon,
  Typography,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import clsx from 'clsx';
import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import { TooltipWrapper } from 'src/components/TooltipWrapper/TooltipWrapper';
import { Truncate } from 'src/components/Truncate/Truncate';
import { supportedPlaceTypes } from 'src/pages/SpacesPage/consts';
import { getIsDesktop } from 'src/store/agent/selectors';
import { getSpacesState } from 'src/store/spaces/selectors';
import { spacesActions } from 'src/store/spaces/slice';
import { MapInfo } from 'src/store/spaces/types';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { plural } from 'src/utils/plural';

import { SpaceBookingObjectOption } from '../../types';

import classes from './SpaceGroup.module.scss';

interface DeskChipProps {
  desk: SpaceBookingObjectOption;
  group: MapInfo;
}

const DeskChip: FC<DeskChipProps> = ({ desk, group }) => {
  const { scrollTo } = useFloorMap();
  const { mapLoading, bookingObjects } = useSelector(getSpacesState);
  const { change } = useForm();
  const id = useField<number | undefined>('id').input.value;
  const groups =
    useField<Map<number, SpaceBookingObjectOption[]>>('groups').input.value;

  const remove = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      const nextGroups: Map<number, SpaceBookingObjectOption[]> = new Map(
        groups,
      );
      const currentGroup = nextGroups.get(group.floorId);
      if (currentGroup) {
        const nextGroup = currentGroup.filter(o => o.id !== desk.id);
        nextGroups.set(group.floorId, nextGroup);
        change('groups', nextGroups);
      }
    },
    [change, desk.id, group.floorId, groups],
  );

  const scroll = useCallback(() => {
    const geoObject = bookingObjects.get(String(desk.id));

    if (!geoObject || !supportedPlaceTypes.has(geoObject.type)) return;

    scrollTo(geoObject);
  }, [bookingObjects, desk.id, scrollTo]);

  const warning =
    desk.value.space && desk.value.space.spaceId !== id
      ? `Место ${desk.value.title} закреплено за "${desk.value.space.title}". При создании нового пространства оно будет перезакреплено.`
      : '';

  return (
    <TooltipWrapper title={warning} disableHover={!warning}>
      <Chip
        label={desk.value.title}
        startAdornment={
          warning ? (
            <Icon data-prevent iconName={Icons.InfoCircled} />
          ) : undefined
        }
        endAdornment={
          <Icon data-prevent onClick={remove} iconName={Icons.Cancel} />
        }
        disabled={mapLoading}
        onClick={scroll}
        data-prevent
        rootClassName={classes.desk}
      />
    </TooltipWrapper>
  );
};

export interface SpaceGroupProps {
  options: SpaceBookingObjectOption[];
  groupInfo: MapInfo;
  onDelete: (groupId: number) => Promise<void>;
}

export const SpaceGroup: FC<SpaceGroupProps> = ({
  options,
  groupInfo,
  onDelete,
}) => {
  const isDesktop = useSelector(getIsDesktop);
  const dispatch = useAppDispatch();

  const [folded, setFolded] = useState(true);

  const { floor } = useSelector(getSpacesState);
  const groupTitle = `${groupInfo.buildingAddress} (этаж\u00A0${groupInfo.title})`;
  const seatCount = `${options.length} ${plural(options.length, {
    zero: 'выбранных мест',
    one: 'выбранное место',
    two: 'выбранных места',
    few: 'выбранных места',
    many: 'выбранных мест',
    other: 'выбранных мест',
  })}`;

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const shouldPrevent = !!(event.target as HTMLElement).closest(
        '[data-prevent]',
      );
      if (shouldPrevent) return;

      setFolded(prev => !prev);
      dispatch(spacesActions.setMap(groupInfo));
    },
    [dispatch, groupInfo],
  );

  useEffect(() => {
    setFolded(floor?.id !== groupInfo.floorId);
  }, [floor?.id, groupInfo.floorId]);

  const deleteGroup = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      onDelete(groupInfo.floorId);
    },
    [groupInfo.floorId, onDelete],
  );

  return (
    <Card
      border='default'
      className={clsx(classes.card, folded && classes.cardFolded)}
      onClick={handleClick}
    >
      {folded ? (
        <div className={classes.containerFolded}>
          <div className={classes.dataFolded}>
            <Truncate variant={isDesktop ? 'subtitle3' : 'subtitle1'}>
              {groupTitle}
            </Truncate>
            <Truncate variant='caption'>{seatCount}</Truncate>
          </div>
          <TooltipWrapper title='Удалить'>
            <Button
              size='small'
              startIcon={
                <Icon
                  iconName={Icons.Delete}
                  onClick={deleteGroup}
                  data-prevent
                />
              }
            />
          </TooltipWrapper>
        </div>
      ) : (
        <div className={classes.containerExpanded}>
          <div className={classes.dataExpanded}>
            {isDesktop ? (
              <Truncate variant='subtitle3'>{groupTitle}</Truncate>
            ) : (
              <Typography variant='subtitle1'>{groupTitle}</Typography>
            )}
            {isDesktop && (
              <Button
                size='small'
                variant='plain'
                onClick={deleteGroup}
                data-prevent
              >
                Удалить
              </Button>
            )}
          </div>
          {!isDesktop && <Divider />}
          <div className={classes.desks}>
            {options
              .sort((first, second) =>
                first.value.title.localeCompare(second.value.title),
              )
              .map(desk => (
                <DeskChip key={desk.id} desk={desk} group={groupInfo} />
              ))}
          </div>
          {!isDesktop && (
            <Button size='medium' onClick={deleteGroup} fullWidth data-prevent>
              Удалить
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};
